import { Injectable, signal } from '@angular/core';
import { StorefrontCart } from '../models/storefront.model';

/** Producto recién agregado al carrito (aviso del header). */
export interface StorefrontCartAddedNotice {
  /** Correlativo: distingue dos agregados seguidos del mismo producto. */
  id: number;
  productName: string;
  variantName: string | null;
  imageUrl: string | null;
  /** Unidades agregadas en esta operación. */
  quantity: number;
}

/**
 * Estado compartido del carrito (badge del header).
 * Se actualiza desde StorefrontApiService en cada operación de carrito.
 */
@Injectable({ providedIn: 'root' })
export class StorefrontCartStateService {
  private readonly _itemCount = signal(0);
  private readonly _lastAdded = signal<StorefrontCartAddedNotice | null>(null);
  private companyId: number | null = null;
  private addedSeq = 0;

  readonly itemCount = this._itemCount.asReadonly();
  readonly lastAdded = this._lastAdded.asReadonly();

  /** #FUNCIONALIDAD Avisa que se agregó un producto: el header muestra el aviso y anima el ícono del carrito. */
  announceAdded(notice: Omit<StorefrontCartAddedNotice, 'id'>): void {
    this._lastAdded.set({ ...notice, id: ++this.addedSeq });
  }

  dismissAdded(): void {
    this._lastAdded.set(null);
  }

  /** Aplica el conteo del carrito para la empresa activa. */
  applyCart(companyId: number, cart: Pick<StorefrontCart, 'itemCount' | 'items'> | null | undefined): void {
    this.companyId = companyId;
    const fromItems = cart && Array.isArray(cart.items) ? cart.items.length : null;
    this._itemCount.set(fromItems ?? cart?.itemCount ?? 0);
  }

  setCount(companyId: number, count: number): void {
    this.companyId = companyId;
    this._itemCount.set(Math.max(0, count));
  }

  /** Reinicia el badge (p. ej. al cambiar de tienda). */
  reset(companyId?: number): void {
    if (companyId != null) this.companyId = companyId;
    this._itemCount.set(0);
    this._lastAdded.set(null);
  }

  activeCompanyId(): number | null {
    return this.companyId;
  }
}
